import React, { useEffect } from 'react';
import Styled from 'styled-components';
import { useRouter } from 'next/router';
import { t } from '@lingui/macro';
import { addDays } from 'date-fns/addDays';
import { isPast } from 'date-fns/isPast';
import { Box } from '@components/layout/Grid';
import Icon from '@components/Icon';
import VideoBox from './videoBox';
import DocumentBox from './DocumentElementBox';
import { VideoBoxNumber, VideoStatus } from '@components/VideoBoxNumber';
import { RepeatIcon } from '@assets/index';
import { VIDEO_KINDS } from '../ProgramOverviewPhaseCollapsable';
import { VideoTag } from '@organisms/cards/ProgramVideoCard';
import iconTooltip, { TooltipText } from '@components/IconTooltip';
import {
  GenericProgram,
  ProgramCategory,
  ProgramDocumentFieldsFragment,
  ProgramLiveEvent,
  ProgramLiveEventElementFieldsFragment,
  ProgramSurveyElementFieldsFragment,
  ProgramTextElementFieldsFragment,
  ProgramVideoFieldsFragment,
  WeeklyProgramQuery,
} from '@gql/generated';
import { HEADER_HEIGHT } from '@lib/constants';
import SurveyElementBox from './SurveyElementBox';
import TextElementBox from './TextElementBox';
import { LiveEventElementBox } from './LiveEventElementBox';

const Wrapper = Styled.div`
  display: block;
  margin-bottom: -2px;
`;

const WeekWrapper = Styled(Box)`
  padding: 10px 5px;
  ${({ showBottomBorder, theme }) =>
    showBottomBorder
      ? `
          :after {
            content: '';
            position: relative;
            display: block;
            height: 2px;
            width: 100%;
            border-bottom: 1px solid ${theme.colors.secondary['gray-3']};
            margin-left: 5px;
            margin-bottom: 10px;
          }
        `
      : null}
`;

const VideoWrapper = Styled.div<{ kind?: string; active?: boolean }>`
  padding: 10px 0 10px 35px;
  border-radius: 8px;

  :hover {
    background-color:rgba(241, 241, 241, .5);
  }
  
  && {
    /* keep this below to overwrite hover */
    ${({ active }) => (active ? 'background-color: #F1F1F1;' : '')}
  }
`;

const Subheader = Styled.p`
  font-size: 14px;
  font-weight: 600;
  margin-top: 0;
  
  ::first-letter {
    text-transform: uppercase;
  }
`;

const IconTooltip = Styled(iconTooltip)`
  z-index: 9;

  ${TooltipText} {
    top: -250%;
    width: 300px;
    pointer-events: none;
  }
`;

const RepeatTag = Styled(VideoTag)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.primary.stone};
  font-weight: bold;
  padding: 6px 8px;
  margin-left: 5px;
  background-color: unset;
  z-index: 0;
`;

const RepeatIconSVG = Styled(RepeatIcon)`
  position: absolute;
  top: -1px;
  left: 0;
  height: 100%;
  width: 100%;
  fill: ${({ theme }) => theme.colors.primary.stone};
`;

const mapStatusTextColor = {
  done: '#ffffff',
  active: '#ffffff',
  open: '#56B9E5',
  locked: '#ffffff',
};

export type WeekElements =
  | ProgramTextElementFieldsFragment
  | ProgramSurveyElementFieldsFragment
  | ProgramDocumentFieldsFragment
  | ProgramVideoFieldsFragment
  | ProgramLiveEventElementFieldsFragment;

declare type Props = {
  lang: string;
  programId: string;
  weekIndex: number;
  weekSlug: string;
  elements: WeekElements[];
  isLast?: boolean;
  programCategory?: GenericProgram['category'];
  // currentWeekSlug & currentElementSlug are from slugs from URL
  currentWeekSlug: string;
  currentElementSlug: string;
};

const VideoItem = ({
  currentWeekSlug,
  currentElementSlug,
  children,
  ...props
}) => {
  const { active } = props;
  useEffect(() => {
    if (active) {
      const vid = document.querySelector(
        `.video-${currentWeekSlug}-${currentElementSlug}`
      ) as HTMLElement;

      setTimeout(() => {
        const container = document.getElementById('program-steps');
        const portal = document.getElementById('MainVideo--Portal');
        if (portal) {
          window.scrollTo({
            top: portal.offsetTop - HEADER_HEIGHT,
            behavior: 'smooth',
          });
        }
        if (container) {
          container.scrollTo({
            top: vid.offsetTop,
            behavior: 'smooth',
          });
        }
      }, 1400);
    }
  }, [active]);

  return <VideoWrapper {...props}>{children}</VideoWrapper>;
};

const WeeklyProgramWeekBox = (props: Props) => {
  const router = useRouter();
  const { params } = router.query;

  const {
    currentWeekSlug,
    currentElementSlug,
    elements,
    isLast,
    lang,
    programCategory,
    programId,
    weekSlug,
  } = props;

  const isCurrentVideo = (videoSlug) => {
    const isCurrent =
      // compare week
      decodeURIComponent(currentWeekSlug) === decodeURIComponent(weekSlug) &&
      // compare video
      decodeURIComponent(currentElementSlug) === decodeURIComponent(videoSlug);
    return isCurrent;
  };

  return (
    <Wrapper data-testid="WeeklyProgramWeekBox">
      <WeekWrapper showBottomBorder={isLast}>
        {elements.map((element, index, arr) => {
          const { title, slug, positionLabel, finished, locked } = element;

          const videoURL = `/${lang}/${programCategory}/${programId}/${weekSlug}/${slug}`;

          let videoStatus: VideoStatus = '',
            isOpenElement = false;

          if (currentWeekSlug === weekSlug && currentElementSlug === slug) {
            isOpenElement = true;
          }

          if (finished === true) {
            videoStatus = 'done';
          } else if (isOpenElement) {
            videoStatus = 'progress';
          }

          let card = null;
          if (element.__typename === 'ProgramDocument') {
            card = (
              <DocumentBox
                key={slug}
                title={title}
                videoId={slug}
                locked={locked}
                isHighlighted={isOpenElement}
                lang={lang}
                tags={[]}
                programCategory={programCategory}
                onClick={() => {
                  router.push(videoURL, null, { scroll: false, shallow: true });
                }}
                meta-data={{
                  last: index === arr.length - 1,
                  finished: finished === true,
                }}
              />
            );
          } else if (element.__typename === 'ProgramSurveyElement') {
            const { survey } = element;
            card = (
              <SurveyElementBox
                key={slug}
                title={title || survey.name}
                videoId={slug}
                locked={locked}
                isHighlighted={isOpenElement}
                lang={lang}
                tags={[]}
                programCategory={programCategory}
                onClick={() => {
                  router.push(videoURL, null, { scroll: false });
                }}
                meta-data={{
                  last: index === arr.length - 1,
                  finished: finished === true,
                }}
              />
            );
          } else if (element.__typename === 'ProgramPartElement') {
            card = (
              <TextElementBox
                key={slug}
                title={title}
                videoId={slug}
                locked={locked}
                isHighlighted={isOpenElement}
                lang={lang}
                tags={[]}
                programCategory={programCategory}
                onClick={() => {
                  router.push(videoURL, null, { scroll: false, shallow: true });
                }}
                meta-data={{
                  last: index === arr.length - 1,
                  finished: finished === true,
                }}
              />
            );
          } else if (element?.__typename === 'ProgramLiveEvent') {
            if (programCategory !== ProgramCategory['Courses']) {
              // hide card if all recordings are expired except course programs
              const dates = element?.liveEvent?.eventDates;
              if (dates?.length > 0) {
                const lastDate = dates[dates.length - 1];
                if (isPast(addDays(new Date(lastDate), 3))) {
                  return null;
                }
              }
            }

            if (element?.liveEvent?.linkToRecordings?.length > 0) {
              //
              return (
                <>
                  {element?.liveEvent?.linkToRecordings
                    // must filter separatly because we need to render sub-header with first video
                    // & first video might be null...
                    ?.filter(({ video }) => !!video?.identifier)
                    ?.map((item, index) => {
                      const { video } = item;

                      const isHighlighted =
                        currentWeekSlug === params[0] &&
                        element.slug === params[1] &&
                        video.identifier === params[2];

                      if (
                        item.isExpired &&
                        programCategory !== ProgramCategory['Courses']
                      ) {
                        return null;
                      }

                      return (
                        <VideoItem
                          key={video?.identifier}
                          data-testid="WeeklyProgramWeekBox--VideoItem"
                          className={`videoWrapper video-${weekSlug}-${slug}`}
                          kind={element['kind']}
                          active={isHighlighted}
                          {...{ currentWeekSlug, currentElementSlug }}
                        >
                          {element['subheader'] && index === 0 ? (
                            <Subheader data-testid="WeeklyProgramWeekBox--Subheader">
                              {element['subheader']}
                            </Subheader>
                          ) : null}
                          <div style={{ position: 'relative' }}>
                            {index === 0 ? (
                              <VideoBoxNumber
                                videoStatus={videoStatus}
                                appearance={programCategory}
                              >
                                {videoStatus === 'done' ? (
                                  <Icon
                                    name="ygb-icon-checkmark"
                                    fontSize="10px"
                                    color={
                                      mapStatusTextColor[
                                        isCurrentVideo(slug)
                                          ? 'active'
                                          : 'locked'
                                      ]
                                    }
                                  />
                                ) : (
                                  positionLabel
                                )}
                              </VideoBoxNumber>
                            ) : null}
                            <VideoBox
                              key={video?.id}
                              videoId={video?.identifier}
                              videoTitle={video?.title || video?.content?.title}
                              videoThumbnail={video?.assets?.thumbnailUrl}
                              videoDuration={video?.meta?.minutes}
                              videoCursorAt={video?.cursorAt}
                              status={element?.locked !== false ? 'locked' : ''}
                              isHighlighted={isHighlighted}
                              lang={lang}
                              tags={[]}
                              programCategory={programCategory}
                              onClick={() => {
                                const url = `/${lang}/${programCategory}/${programId}/${weekSlug}/${element.slug}/${video.identifier}`;
                                router.push(url, null, {
                                  scroll: false,
                                  shallow: true,
                                });
                              }}
                            />
                          </div>
                        </VideoItem>
                      );
                    })}
                </>
              );
            } else {
              card = (
                <LiveEventElementBox
                  key={slug}
                  lang={lang}
                  // TODO: fix this type (create fragment)
                  element={element as ProgramLiveEvent}
                  isHighlighted={isOpenElement}
                  programCategory={programCategory}
                  weekSlug={weekSlug}
                  meta-data={{
                    bonus: String(positionLabel).toLowerCase() === 'b',
                    last: index === arr.length - 1,
                    finished: finished === true,
                  }}
                />
              );
            }
          } else if (element.__typename === 'ProgramVideo') {
            const { videoKind, finished, locked, video, repeatRecomendation } =
              element;
            let videoTags = [];

            if (locked) {
              videoTags = [t`metadata.preview`];
            } else {
              if (VIDEO_KINDS.hasOwnProperty(videoKind)) {
                videoTags = [VIDEO_KINDS[videoKind]];
              }
              if (repeatRecomendation) {
                videoTags.push(
                  <IconTooltip
                    key="repeat"
                    translationId={t`program.video.repeat_explanation`}
                    icon={
                      <RepeatTag>
                        <RepeatIconSVG />
                        {repeatRecomendation}
                      </RepeatTag>
                    }
                  />
                );
              }
            }
            card = (
              <VideoBox
                key={slug}
                videoId={slug}
                videoTitle={title || video.content.title}
                videoThumbnail={video.assets.thumbnailUrl}
                videoDuration={video.meta.minutes}
                videoCursorAt={video.cursorAt}
                status={locked !== false ? 'locked' : ''}
                isHighlighted={isOpenElement}
                lang={lang}
                tags={videoTags}
                programCategory={programCategory}
                onClick={() => {
                  router.push(videoURL, null, { scroll: false, shallow: true });
                }}
                meta-data={{
                  bonus: String(positionLabel).toLowerCase() === 'b',
                  last: index === arr.length - 1,
                  finished: finished === true,
                }}
              />
            );
          } else {
            return null;
          }

          return (
            <VideoItem
              data-testid="WeeklyProgramWeekBox--VideoItem"
              key={index}
              className={`videoWrapper video-${weekSlug}-${slug}`}
              kind={element['kind']}
              active={isOpenElement}
              {...{ currentWeekSlug, currentElementSlug }}
            >
              {element['subheader'] ? (
                <Subheader data-testid="WeeklyProgramWeekBox--Subheader">
                  {element['subheader']}
                </Subheader>
              ) : null}
              <div style={{ position: 'relative' }}>
                <VideoBoxNumber
                  videoStatus={videoStatus}
                  appearance={programCategory}
                >
                  {videoStatus === 'done' ? (
                    <Icon
                      name="ygb-icon-checkmark"
                      fontSize="10px"
                      color={
                        mapStatusTextColor[
                          isCurrentVideo(slug) ? 'active' : 'locked'
                        ]
                      }
                    />
                  ) : (
                    positionLabel
                  )}
                </VideoBoxNumber>
                {card}
              </div>
            </VideoItem>
          );
        })}
      </WeekWrapper>
    </Wrapper>
  );
};

export default WeeklyProgramWeekBox;
